var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from "react";
import { useApolloClient } from "@apollo/client";
import { Alert, Button, Snackbar } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AuthContext } from "@/providers/AuthContextProvider";
import { useTenantStore } from "@/providers/TenantContextProvider";
import { createSigningApi } from "@/lib/signing/api";
import { takeResume } from "@/lib/signing/request";
import { SigningRequestPanel } from "./SigningRequestPanel";
var SigningContext = createContext(null);
/** Opens a signing request's panel from anywhere below `SigningProvider`. */
export var useSigningRequest = function () {
    var context = useContext(SigningContext);
    if (!context) {
        throw new Error("useSigningRequest must be used within a SigningProvider");
    }
    return context;
};
var RenderFailure = function (_a) {
    var open = _a.open, onClose = _a.onClose;
    var t = useTranslation().t;
    return (_jsx(Snackbar, { open: open, anchorOrigin: { vertical: "bottom", horizontal: "right" }, children: _jsx(Alert, { severity: "error", action: _jsx(Button, { color: "inherit", size: "small", onClick: onClose, children: t("signing.widget.close") }), children: t("signing.widget.renderError") }) }));
};
/**
 * Keeps a request that can't be shown (a malformed answer, a failing
 * completion action) from unmounting the portal around it.
 */
var SigningErrorBoundary = /** @class */ (function (_super) {
    __extends(SigningErrorBoundary, _super);
    function SigningErrorBoundary() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = { failed: false };
        return _this;
    }
    SigningErrorBoundary.getDerivedStateFromError = function () {
        return { failed: true };
    };
    SigningErrorBoundary.prototype.render = function () {
        return this.state.failed ? (_jsx(RenderFailure, { open: this.props.open, onClose: this.props.onClose })) : (this.props.children);
    };
    return SigningErrorBoundary;
}(React.Component));
export { SigningErrorBoundary };
/**
 * Hosts the signing request panel and its dialog, opened with
 * `useSigningRequest().open(id)`. After a handover, once the next member has
 * signed in, it reopens the request and its dialog for them.
 */
export var SigningProvider = function (_a) {
    var _b;
    var children = _a.children, api = _a.api, completionActions = _a.completionActions, storage = _a.storage;
    var client = useApolloClient();
    var _c = useContext(AuthContext), isAuthenticated = _c.isAuthenticated, userId = _c.userId, isAuthorized = _c.isAuthorized;
    var tenantId = useTenantStore()[0];
    // Read at each call, so the api (and the actions it remembers) outlives a token refresh.
    var holdsRef = useRef(function () { return false; });
    holdsRef.current = function (permission) { return isAuthorized(true, tenantId, permission); };
    var signingApi = useMemo(function () {
        return api !== null && api !== void 0 ? api : createSigningApi(client, undefined, {
            holds: function (permission) { return holdsRef.current(permission); },
        });
    }, [api, client]);
    var _d = useState(null), opened = _d[0], setOpened = _d[1];
    var _e = useState(false), visible = _e[0], setVisible = _e[1];
    var open = useCallback(function (requestId, options) {
        if (options === void 0) { options = {}; }
        setOpened(function (previous) { var _a; return (__assign(__assign({ requestId: requestId }, options), { nonce: ((_a = previous === null || previous === void 0 ? void 0 : previous.nonce) !== null && _a !== void 0 ? _a : 0) + 1 })); });
        setVisible(true);
    }, []);
    var close = useCallback(function () { return setVisible(false); }, []);
    useEffect(function () {
        if (!isAuthenticated || !userId || !tenantId)
            return;
        var target = storage !== null && storage !== void 0 ? storage : null;
        try {
            target !== null && target !== void 0 ? target : (target = window.sessionStorage);
        }
        catch (_a) {
            return;
        }
        var resume = takeResume(target, { tenantId: tenantId });
        if (resume) {
            open(resume.requestId, { sign: true, eventId: resume.eventId });
        }
    }, [isAuthenticated, userId, tenantId, storage, open]);
    var context = useMemo(function () { var _a; return ({ open: open, close: close, requestId: visible ? ((_a = opened === null || opened === void 0 ? void 0 : opened.requestId) !== null && _a !== void 0 ? _a : null) : null }); }, [open, close, visible, opened]);
    var actions = opened
        ? ((_b = opened.completionActions) !== null && _b !== void 0 ? _b : (function (data) { var _a; return (_a = completionActions === null || completionActions === void 0 ? void 0 : completionActions[data.request.action]) === null || _a === void 0 ? void 0 : _a.call(completionActions, data); }))
        : undefined;
    return (_jsxs(SigningContext.Provider, { value: context, children: [children, opened ? (_jsx(SigningErrorBoundary, { open: visible, onClose: close, children: _jsx(SigningRequestPanel, { requestId: opened.requestId, api: signingApi, open: visible, onClose: close, autoSign: opened.sign, completionActions: actions, onChange: opened.onChange, storage: storage, expectedEventId: opened.eventId }) }, opened.nonce)) : null] }));
};
